import { Component, type ErrorInfo, type ReactNode } from 'react';
import { createLogger } from '@origo/logger';
import styles from './AppErrorBoundary.module.css';

const log = createLogger('AppErrorBoundary');

interface Props {
    readonly children: ReactNode;
}

interface State {
    readonly hasError: boolean;
}

/**
 * Siste skanse for uhåndterte render-feil (etappe 0, AP0.6 — jf.
 * docs/feilhandteringspolicy.md): logger feilen med kontekst og viser en
 * enkel feilside med mulighet for å prøve igjen, i stedet for hvit skjerm.
 *
 * Tekstene er bevisst hardkodet norsk: boundaryen skal fungere selv når
 * feilen ligger i i18n-oppsettet.
 */
export class AppErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        log.error('Uhåndtert feil i React-treet', error, info.componentStack);
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div className={styles.wrapper} role="alert">
                <h1 className={styles.heading}>Noe gikk galt</h1>
                <p className={styles.message}>
                    Det oppsto en uventet feil. Prøv å laste siden på nytt — hvis
                    problemet vedvarer, forsøk igjen senere.
                </p>
                <button
                    type="button"
                    className={styles.retry}
                    onClick={() => window.location.reload()}
                >
                    Last siden på nytt
                </button>
            </div>
        );
    }
}
